import React, { useCallback, useEffect, useState } from "react";
import "./App.css";
import { DataSet, DataSourceList, GroupDataSet, groupKeysList, Point3D, SharedDataType } from "./cosmo-graph/model";
import { GraphContext } from "./cosmo-graph/data";
import { ClusterPanel } from "./cosmo-graph/cluster-panel";
import { NotePanel } from "./cosmo-graph/node-panel";
import { CrPanel } from "./cosmo-graph/cr-panel";
import { SearchPanel } from "./cosmo-graph/search-panel";
import { AboutPanel } from "./cosmo-graph/about-panel";
import CloudCanvas from './components/CloudCanvas';
import { generateSecondGroup, processCsvData } from "./services/generator";

function App() {
  const [sharedData, setSharedData] = React.useState<SharedDataType>({
    source: "",
    subType: "",
  });

  const [dataSource, setDataSource] = React.useState<string>('');
  const [groupIndex, setGroupIndex] = React.useState<number>(0);

  const [clusterData, setClusterData] = React.useState<any>({});
  const [nodeData, setNodeData] = React.useState<any>({});
  const [colorList, setColorList] = React.useState<any>([]);
  const [rangeList, setRangeList] = React.useState<any>([]);

  const [pointsData, setPointsData] = useState<Point3D[]>([]);
  const [groupData, setGroupData] = useState<GroupDataSet>({});

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [secondGroupData, setSecondGroupData] = useState<DataSet[]>([]);

  const [showUnknown, setShowUnknown] = useState<boolean>(true);
  const [searchText, setSearchText] = React.useState<string>('');
  const [searchGroup, setSearchGroup] = React.useState<string>('');

  // Interaction State
  const [selectedPoint, setSelectedPoint] = useState<Point3D | null>(null);
  const [hoveredPointInfo, setHoveredPointInfo] = useState<{ id: string; x: number; y: number } | null>(null);

  const [progress, setProgress] = useState<number>(0);
  const [manualPaused, setManualPaused] = useState<boolean>(false);

  const initData = useCallback((groupIndex: number) => {
    setLoading(true);
    setError(null);
    setProgress(0);

    setTimeout(async () => {
      try {
        const url = 'pfam.csv.gz'; // dataSource || DataSourceList[0].groupIndex;
        const response = await fetch(`data/${url}`);
        if (!response.body) {
          throw new Error('ReadableStream not supported');
        }

        const contentLength = response.headers.get('Content-Length');
        const totalLength = contentLength ? parseInt(contentLength, 10) : 0;

        const reader = response.body.getReader();
        const chunks = [];
        let receivedLength = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            break;
          }

          chunks.push(value);
          receivedLength += value.length;

          if (totalLength > 0) {
            setProgress(Math.round((receivedLength / totalLength) * 100));
          }
        }

        const blob = new Blob(chunks);
        const ds = new (window as any).DecompressionStream("gzip");
        const decompressedStream = blob.stream().pipeThrough(ds);
        const decompressedResponse = new Response(decompressedStream);
        const csvData = await decompressedResponse.text();

        const result = processCsvData(csvData, groupIndex);
        setPointsData(result.points);
        setGroupData(result.groupData);

        setSharedData({
          source: sharedData.source,
          subType: sharedData.subType,
          leftGroupKey: groupKeysList[0],
        });

        setLoading(false);
      } catch (err) {
        console.error("Failed to load data", err);
        setError("Failed to load data");
        setLoading(false);
      }
    }, 100);
  }, []);

  useEffect(() => {
    initData(groupIndex);
  }, [initData, groupIndex]);

  useEffect(() => {
    if (!sharedData.leftGroupKey || !sharedData.leftSelectedKey) {
      return;
    }

    const restList = groupKeysList.filter((key) => key !== sharedData.leftGroupKey);
    let rightGroupKey = sharedData.rightGroupKey || restList[0];
    if (!restList.includes(rightGroupKey)) {
      rightGroupKey = restList[0];
    }

    // right panel list, based on selected item on left panel
    const secondGroup = generateSecondGroup(pointsData, sharedData.leftGroupKey, sharedData.leftSelectedKey, rightGroupKey);
    setSecondGroupData(secondGroup.dataList);

    // set points colors based on right panel list
    pointsData.forEach(p => {
      const name = p[`:${rightGroupKey}Name`] as string;
      if (secondGroup.groupData[name]) {
        p[`:Right:Color`] = secondGroup.groupData[name].color;
      }
    })
    setPointsData([...pointsData]);
  }, [sharedData.leftGroupKey, sharedData.leftSelectedKey, sharedData.rightGroupKey]);

  const handleHover = (point: Point3D | null, x: number, y: number) => {
    if (point) {
      // setHoveredPointInfo({ id: point.originalId, x, y });
    } else {
      setHoveredPointInfo(null);
    }
  };

  return (
    <div className="root-container">
      <GraphContext.Provider
        value={{
          clusterData,
          setClusterData,
          nodeData,
          setNodeData,
          sharedData,
          setSharedData,
          colorList,
          setColorList,
          rangeList,
          setRangeList,
          groupData,
          setGroupData,
          secondGroupData,
          setSecondGroupData,
          dataSource,
          setDataSource,
          groupIndex,
          setGroupIndex,
          showUnknown,
          setShowUnknown,
          searchText,
          setSearchText,
          searchGroup,
          setSearchGroup,
        }}
      >
        {loading && (
          <div className='app-loading'>
            <div className='progress-track'>
              <div className='progress-fill' style={{ width: `${progress}%` }}></div>
              <div className='progress-text'>{progress}%</div>
            </div>
          </div>
        )}
        <CloudCanvas
          data={pointsData}
          paused={selectedPoint !== null || manualPaused}
          onPointDoubleClick={setSelectedPoint}
          onHover={handleHover}
          showUnknown={showUnknown}
          searchText={searchText}
          searchGroup={searchGroup}
        />

        {!selectedPoint && (
          <button className="play-pause-btn" onClick={() => setManualPaused(!manualPaused)} title={manualPaused ? "Play" : "Pause"}>
            {manualPaused ? '▶' : '⏸'}
          </button>
        )}

        {!loading && <SearchPanel pointsData={pointsData} searchText={searchText} searchGroup={searchGroup} setSearchText={setSearchText} setSearchGroup={setSearchGroup} />}
        <ClusterPanel />
        <NotePanel point={selectedPoint} onClose={() => setSelectedPoint(null)} />
        {/* <RangePanel /> */}
        {!loading && <CrPanel side="left" />}
        <CrPanel side="right" />
        <AboutPanel />

      </GraphContext.Provider>
    </div>
  );
}

export default App;
